import {Component, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '@env';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {UserService} from '@emm/core/services/user.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {DialogService} from '@emm/shared/services/dialog.service';
import {catchError, filter, finalize, switchMap, tap} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {BehaviorSubject, of} from 'rxjs';

interface ChangeSetsGroup {
    effectiveDate: string;
    subMenuIds: string[];
    consumers: string[];
}

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-cache-operations',
    styleUrls: ['./operations.component.scss'],
    templateUrl: './operations.component.html',
    standalone: false
})
export class OperationsComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    cleared = new BehaviorSubject<boolean>(false);
    dataCopied = new BehaviorSubject<boolean>(false);
    backupStarted = new BehaviorSubject<boolean>(false);
    protected readonly environment = environment;
    isProd = environment.name === 'prod';
    isStg = environment.name === 'stg';
    isAdminOrSuperAdmin = false;
    isSuperAdmin = false;

    showMoveDates = false;
    changeSets$ = new BehaviorSubject<ChangeSetsGroup[]>([]);
    selectedDateIndex = -1;
    readonly moveDatesColumns = ['select', 'effectiveDate', 'submenus', 'consumers'];
    isLoadingChangeSets = true;
    isMovingChangeSets = false;
    selectedDate = null;
    private beforeUnloadListenerAttached = false;

    constructor(private managementApi: ManagementApiService,
                private userService: UserService,
                private notificationService: NotificationService,
                private dialogService: DialogService,
    ) {
        this.isAdminOrSuperAdmin = (this.userService.hasAdminRole() || this.userService.hasSuperAdminRole());
        this.isSuperAdmin = this.userService.hasSuperAdminRole();
        this.destroyRef.onDestroy(() => {
            this.updateBeforeUnloadListener(false);
        });
    }

    ngOnInit(): void {
        this.managementApi.getAllChangeSetInfo().pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(changeSets => {
                this.changeSets$.next(changeSets);
                this.isLoadingChangeSets = false;
            }),
            catchError((error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                return of(null);
            }))
            .subscribe();
    }

    clearCache(): void {
        this.dialogService.openConfirmDialog('Are you sure you want to', 'Clear Cache', '')
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() => this.managementApi.clearCache()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.cleared.next(true);
                    this.notificationService.success('Your cache was deleted!');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    canCopyProdData(): boolean {
        return (!this.isProd && !this.isStg && this.isAdminOrSuperAdmin) || (this.isStg && this.isSuperAdmin);
    }

    canStartDatabaseBackup(): boolean {
        return (!this.isProd && this.isAdminOrSuperAdmin) || (this.isProd && this.isSuperAdmin);
    }

    handleCopyProdDataClicked() {
        if (!this.canCopyProdData()) { return; }
        this.dialogService.openConfirmDialog('Are you sure you want to', 'Copy Prod Data', '')
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() => this.managementApi.copyProdData()),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
                next: () => {
                    this.dataCopied.next(true);
                    this.notificationService.success('Data copy request submitted!');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    handleStartDatabaseBackupClicked() {
        if (!this.canStartDatabaseBackup()) { return; }
        this.dialogService.openConfirmDialog('Are you sure you want to', 'start a database backup', '')
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                switchMap(() => this.managementApi.startDatabaseBackup()),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
                next: () => {
                    this.backupStarted.next(true);
                    this.notificationService.success('Database backup started!');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    handleMoveEffectiveDateClicked(): void {
        this.showMoveDates = true;
        this.selectedDateIndex = -1;
    }

    isDateSelected(index: number): boolean {
        return this.selectedDateIndex >= 0 && index <= this.selectedDateIndex;
    }

    isDateLocked(index: number): boolean {
        return this.selectedDateIndex >= 0 && index < this.selectedDateIndex;
    }

    onDateCheckboxChange(index: number, checked: boolean): void {
        const changeSets = this.changeSets$.value;
        const selectedChangeSet = changeSets[index];
        if (checked && selectedChangeSet) {
            this.selectedDateIndex = index;
            this.selectedDate = selectedChangeSet.effectiveDate;
        } else if (index === this.selectedDateIndex) {
            this.selectedDateIndex = -1;
            this.selectedDate = null;
        }
    }

    formatDateForDisplay(dateStr: string): string {
        const d = new Date(dateStr);
        return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
    }

    moveDatesToToday(): void {
        const selectedDates = this.changeSets$.value
            .filter((_, i) => i <= this.selectedDateIndex)
            .map(g => g.effectiveDate);
        const formatted = selectedDates.map(d => this.formatDateForDisplay(d)).join(', ');

        this.dialogService.openConfirmDialog(
            'Are you sure you want to move future effective dates',
            `(${formatted})`,
            'for all impacted submenus to today?',
            'You will need to wait 5 minutes for changes to take effect.'
        ).afterClosed().pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(RA.isTruthy),
            switchMap(() => {
                this.isMovingChangeSets = true;
                this.updateBeforeUnloadListener(true);
                return this.managementApi.rescheduleSelectedChangeSets(this.selectedDate);
            }),
            switchMap(() => this.managementApi.getAllChangeSetInfo()),
            finalize(() => {
                this.isMovingChangeSets = false;
                this.updateBeforeUnloadListener(false);
            })
        ).subscribe({
            next: (changeSets: ChangeSetsGroup[]) => {
                this.changeSets$.next(changeSets);
                this.notificationService.success('Future effective dates moved to today!');
                this.selectedDateIndex = -1;
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    private readonly handleBeforeUnload = (event: BeforeUnloadEvent): string => {
        const message = 'Rescheduling change sets is in progress. Please stay on the page until it finishes.';
        event.preventDefault();
        event.returnValue = message;
        return message;
    };

    private updateBeforeUnloadListener(shouldAdd: boolean): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (shouldAdd && !this.beforeUnloadListenerAttached) {
            window.addEventListener('beforeunload', this.handleBeforeUnload);
            this.beforeUnloadListenerAttached = true;
        } else if (!shouldAdd && this.beforeUnloadListenerAttached) {
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
            this.beforeUnloadListenerAttached = false;
        }
    }
}
